import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Typography from "../../components/Typography";
import { theme } from "../../theme";
import MainNavbar from "../../components/Navbar/MainNavbar";
import { useLocation, useNavigate } from "react-router-dom";
import axiosClient from "../../api/axiosClient";
import NotificationCard from "../../components/Notifications/NotificationCard";
import {
	buildNotificationTitle,
	buildNotificationActions,
	buildNotificationIcon,
	buildNotificationPrimaryAction,
} from "../../utils/notificationUtils";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import Button from "../../components/Button/Button";
import SettingsOutlineIcon from "../../components/Icons/SettingsOutlineIcon";
import { formatNotificationTimestamp } from "../../utils/dateUtils";
import { openUserSettingsModal } from "../../utils/settingsNavigation";

const UserNotifications = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { t, i18n } = useTranslation("userProfile");
	const { user, followingSet, refreshFollowing } = useAuth();
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		const load = async () => {
			try {
				const { data } = await axiosClient.get("/api/activity/feed?limit=20");
				setItems(data.activities || []);
			} catch (e) {
				setError("No se pudo cargar la actividad");
			} finally {
				setLoading(false);
			}
		};
		load();
	}, []);

	// followingSet ahora proviene del AuthContext

	return (
		<>
			<MainNavbar />
			<PageWrapper>
				<Container>
					<PageHeader>
						<Typography
							as="h1"
							$variant="title-4-semibold"
							$variantTablet="title-5-semibold"
							$variantMobile="body-1-semibold"
						>
							{t("notifications.title")}
						</Typography>
						{/* <Typography $variant="body-2-medium" color={theme.colors.defaultStrong}>
              Actividad reciente de personas que sigues y eventos cercanos.
            </Typography> */}
						<Button
							$variant="ghost"
							onClick={() =>
								openUserSettingsModal({
									navigate,
									location,
									userId: user?.id || user?._id,
								})
							}
						>
							<SettingsOutlineIcon
								width={16}
								height={16}
								fill={theme.colors.defaultMain}
							/>
							{t("notifications.configure")}
						</Button>
					</PageHeader>
					{loading && (
						<EmptyState>
							<Typography
								$variant="body-1-medium"
								color={theme.colors.defaultMain}
							>
								{t("notifications.loading")}
							</Typography>
						</EmptyState>
					)}
					{!loading && items.length === 0 && (
						<EmptyState>
							<Typography
								$variant="body-1-medium"
								color={theme.colors.defaultMain}
							>
								{t("notifications.empty")}
							</Typography>
						</EmptyState>
					)}
					{!loading && items.length > 0 && (
						<List>
							{items.map((it) => (
								<NotificationCard
									key={it._id}
									title={buildNotificationTitle(it)}
									timestamp={formatNotificationTimestamp(
										it.createdAt,
										i18n.language,
									)}
									actions={buildNotificationActions(
										it,
										navigate,
										followingSet || new Set(),
										() => refreshFollowing(),
									)}
									icon={buildNotificationIcon(it)}
									primaryAction={buildNotificationPrimaryAction(
										it,
										navigate,
										true,
									)}
								/>
							))}
						</List>
					)}
				</Container>
			</PageWrapper>
		</>
	);
};

export default UserNotifications;

const PageWrapper = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.sizing.sm};
  padding: ${(props) => props.theme.sizing.sm} ${(props) => props.theme.sizing.md};
  width: 100%;
  max-width: 1400px;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
  }
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: ${(props) => props.theme.sizing.xxs};
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 160px;
  width: 100%;
  border: 1px dashed ${(props) => props.theme.border.defaultSubtle};
  border-radius: ${(props) => props.theme.radius.xs};
  background-color: ${(props) => props.theme.fill.defaultSubtle};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  // gap: ${(props) => props.theme.sizing.xs};
  width: 100%;
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  overflow: hidden;
`;
